import { Component, Input, Output, EventEmitter, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize, Subscription } from 'rxjs';

import { MeterService } from '@services/meter.service';
import { MeterDetails } from '@models/meter.model';
import { UserDetails } from '@services/login.service';
import { RelationshipType } from '@models/consent.model';
import { getRelationshipForLoa, formatDateForDisplay } from '@utils/meter.utils';

export interface DigitalSignatureData {
  selectedMeters: MeterDetails[];
  userDetails: UserDetails | null;
  relationshipType: RelationshipType;
}

@Component({
  selector: 'app-digital-signature',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  template: `
    <div class="consent-option">
      <h6>Digital Signature</h6>
      <p>
        Sign the Letter of Authority digitally using our secure e-signing service.
      </p>

      <!-- Show verified state -->
      @if (isVerified) {
        <div class="signature-verified">
          <button mat-flat-button color="primary" disabled>
            <mat-icon>check_circle</mat-icon>
            Signature Verified
          </button>
          <p class="verified-text">
            <mat-icon class="success-icon">check_circle</mat-icon>
            Your digital signature has been successfully verified.
          </p>
        </div>
      }

      <!-- Show sign button when not verified -->
      @if (!isVerified) {
        <div>
          <button mat-flat-button color="primary" [disabled]="isRequesting" (click)="requestDigitalSignature()">
            <mat-icon>edit</mat-icon>
            Sign Digitally
          </button>
          @if (isRequesting) {
            <p class="loading-text">
              <mat-spinner diameter="20"></mat-spinner>
              Preparing digital signature...
            </p>
          }
        </div>
      }
    </div>

    <!-- Iframe overlay for digital signing -->
    @if (showSigningOverlay) {
      <div class="signing-overlay">
        <div class="signing-overlay-header">
          <span>Sign Document</span>
          <button mat-icon-button (click)="closeSigningOverlay()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="signing-overlay-body">
          @if (!iframeSrc) {
            <div class="signing-overlay-loading">
              <mat-spinner diameter="40"></mat-spinner>
              <p>Loading signing page...</p>
            </div>
          }
          @if (iframeSrc) {
            <iframe [src]="iframeSrc" class="signing-iframe"></iframe>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    .consent-option {
      // border: 1px solid #e0e0e0;
      border-radius: 8px;
      // padding: 16px;
      // margin-bottom: 16px;
    }

    .consent-option h6 {
      margin: 0 0 8px 0;
      color: #333;
      font-weight: 600;
    }

    .consent-option p {
      margin: 0 0 16px 0;
      color: #666;
      line-height: 1.5;
    }

    .signature-verified {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .verified-text {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      color: #4caf50;
      font-weight: 500;
    }

    .success-icon {
      color: #4caf50;
    }

    .loading-text {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 8px 0 0 0;
      color: #666;
    }

    button[mat-flat-button] {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    button[mat-flat-button]:disabled {
      background-color: #4caf50 !important;
      color: white !important;
    }

    .signing-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      z-index: 1000;
      display: flex;
      flex-direction: column;
    }

    .signing-overlay-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 16px;
      background: #fff;
      border-bottom: 1px solid #e0e0e0;
      font-weight: 600;
      font-size: 16px;
    }

    .signing-overlay-body {
      flex: 1;
      display: flex;
      background: #fff;
    }

    .signing-overlay-loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      gap: 16px;
      color: #666;
    }

    .signing-iframe {
      width: 100%;
      height: 100%;
      border: none;
    }
  `]
})
export class DigitalSignatureComponent implements OnDestroy {
  @Input() isVerified = false;
  @Input() signatureData!: DigitalSignatureData;

  @Output() consentReceived = new EventEmitter<string>();
  @Output() loadingStateChange = new EventEmitter<boolean>();
  @Output() error = new EventEmitter<string>();

  private meterService = inject(MeterService);
  private sanitizer = inject(DomSanitizer);

  isRequesting = false;
  showSigningOverlay = false;
  iframeSrc: SafeResourceUrl | null = null;

  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingSub: Subscription | null = null;
  private readonly POLL_INTERVAL = 3000;
  private readonly MAX_ATTEMPTS = 40;

  ngOnDestroy(): void {
    this.stopPolling();
  }

  requestDigitalSignature(): void {
    const selectedMeters = this.signatureData?.selectedMeters;
    if (!selectedMeters || selectedMeters.length === 0) {
      this.error.emit('No meters selected for digital signature');
      return;
    }

    this.isRequesting = true;
    this.loadingStateChange.emit(true);
    this.showSigningOverlay = true;
    this.iframeSrc = null;

    const sinceDate = new Date();
    const sinceDateFormatted = formatDateForDisplay(sinceDate);

    this.meterService.requestDigitalSignatureConsent({
      companyName: this.signatureData.userDetails?.companyName || 'Perse',
      fullName: this.signatureData.userDetails?.firstName + ' ' + this.signatureData.userDetails?.lastName || '<user_full_name>',
      email: this.signatureData.userDetails?.email || '<user_email>',
      role: this.signatureData.userDetails?.role || 'Customer',
      relationship: getRelationshipForLoa(this.signatureData.relationshipType || RelationshipType.OCCUPIER),
      meters: selectedMeters.map(meter => ({
        mpan: meter.meterNo,
        address: meter.addressAsLine
      })),
      sinceDate: sinceDateFormatted
    })
      .pipe(
        finalize(() => {
          this.isRequesting = false;
          this.loadingStateChange.emit(false);
        })
      )
      .subscribe({
        next: (response: { accessLink: string, consentId: number }) => {
          console.log('[DigitalSignature] API response received:', response);
          if (response && response.accessLink) {
            this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(response.accessLink);
            this.startPolling(response.consentId.toString());
          } else {
            console.error('[DigitalSignature] No accessLink in response:', response);
            this.closeSigningOverlay();
            this.error.emit('Failed to generate digital signature link');
          }
        },
        error: (err: unknown) => {
          this.closeSigningOverlay();
          console.error('Error requesting digital signature:', err);
          this.error.emit('An error occurred while requesting digital signature');
        }
      });
  }

  closeSigningOverlay(): void {
    this.stopPolling();
    this.showSigningOverlay = false;
    this.iframeSrc = null;
  }

  private startPolling(consentId: string): void {
    let attempts = 0;

    const poll = () => {
      if (attempts >= this.MAX_ATTEMPTS) {
        console.warn('[DigitalSignature] Polling timed out');
        this.closeSigningOverlay();
        this.error.emit('Verification timed out. Please try again.');
        return;
      }

      attempts++;
      this.pollingSub = this.meterService.checkDigitalSignatureStatus(consentId)
        .subscribe({
          next: (response: { consentStatus: string }) => {
            const status = response.consentStatus;
            console.log(`[DigitalSignature] Poll #${attempts}: status=${status}`);

            if (status === 'signed') {
              this.closeSigningOverlay();
              this.consentReceived.emit(consentId);
            } else if (status === 'pending') {
              this.pollingTimer = setTimeout(poll, this.POLL_INTERVAL);
            } else {
              // error or unexpected status
              this.closeSigningOverlay();
              this.error.emit('Digital signature verification failed. Please try again.');
            }
          },
          error: (err: unknown) => {
            console.error('[DigitalSignature] Polling error:', err);
            this.closeSigningOverlay();
            this.error.emit('Error checking signature status. Please try again.');
          }
        });
    };

    // Initial delay before first poll
    this.pollingTimer = setTimeout(poll, 2000);
  }

  private stopPolling(): void {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
      this.pollingSub = null;
    }
  }
}
